import { ORCHESTRATION_PROVIDER } from '@common/Fabric/types/orchestration';
import { FabricProviderInfoForMatching, isFabricProviderMatching } from '@common/Fabric/utils';
import { ErrorTooltip } from '@common/StepperPage/ErrorTooltip';
import { TabError } from '@common/StepperPage/hooks';
import { theme } from '@prophecy/theme';
import { Button, isFormDirty, Stack, StackItem } from '@prophecy/ui-v3';
import { afterAnimationFrame } from '@prophecy/utils/dom';
import { useDependantState, usePersistentCallback } from '@prophecy/utils/react/hooks';
import { usePrompt } from '@prophecy/utils/react/router';
import { AIRFLOW_PROVIDER } from 'frontend/core/src/constants/airflow';
import { useState } from 'react';
import styled from 'styled-components';

import useIsUnifiedFabric from '../hooks/useIsUnifiedFabric';
import useUpdateFabric from '../hooks/useUpdateFabric';
import { StyledSpinner } from '../styles';
import { FabricTokens } from '../tokens';
import { FABRIC_TABS, FabricProviderType, FormContexts, SPARK_PROVIDER } from '../types';
import ProviderForm from './ProviderForm';
import { useTestConnection } from './TestConnectionContext';

const StyledStack = styled(Stack)`
  min-height: 100%;
`;

const Display = styled.div<{ show: boolean }>`
  height: 100%;
  display: ${({ show }) => (show ? 'block' : 'none')};
`;

const getFabricUrl = (pathname: string) => {
  return pathname.split('/').slice(0, -1).join('/');
};

export const ProviderTab = ({
  fabricId,
  formContexts,
  isLoading: isFabricLoading,
  disabledMessage,
  refreshWarehouseConnection,
  setFormUpdateLock
}: {
  fabricId: string;
  formContexts: FormContexts;
  isLoading?: boolean;
  disabledMessage?: string;
  refreshWarehouseConnection?: () => Promise<void>;
  setFormUpdateLock?: (locked: boolean) => void;
}) => {
  const [errors, setErrrors] = useState<TabError>();

  const { testConnection, isLoading: testConnectionLoading, shouldTestConnection } = useTestConnection();

  const isUnifiedFabric = useIsUnifiedFabric(formContexts);
  const provider = formContexts[FABRIC_TABS.providers].getValues('provider');

  const isUnifiedDbxFabric = isUnifiedFabric && provider === SPARK_PROVIDER.DATABRICKS;
  const { updateExistingFabric, isLoading } = useUpdateFabric(fabricId, formContexts, true);

  const onUpdateFabric = () => {
    formContexts[FABRIC_TABS.providers].handleSubmit(async (formData) => {
      if (shouldTestConnection && !(await testConnection())) return;
      setFormUpdateLock?.(true);
      try {
        await updateExistingFabric();
        await refreshWarehouseConnection?.();
        formContexts[FABRIC_TABS.providers].reset(formData);
      } finally {
        setFormUpdateLock?.(false);
      }
    })();
  };

  usePrompt(
    (prevLocation, location) => {
      if (
        prevLocation.pathname === location.pathname ||
        getFabricUrl(prevLocation.pathname) !== getFabricUrl(location.pathname) ||
        (isUnifiedDbxFabric && prevLocation.pathname !== location.pathname)
      )
        return 'Do you want to leave this page without saving your changes?';
    },
    isFormDirty(
      formContexts[FABRIC_TABS.providers],
      isUnifiedFabric ? ['providerV2'] : ['fabricProviderType', 'provider']
    ),
    {
      onOk() {
        formContexts[FABRIC_TABS.providers].reset();
      }
    }
  );

  const fabricProviderType = formContexts[FABRIC_TABS.providers].getValues('fabricProviderType');
  // initialFabricProviderTypes is used to store the initial fabric provider types when the fabric is loaded for unified fabrics
  const initialFabricProviderTypes = formContexts[FABRIC_TABS.providers].getValues('initialFabricProviderTypes');
  const providerInfo: FabricProviderInfoForMatching = {
    fabricProviderType: initialFabricProviderTypes?.length ? initialFabricProviderTypes : [fabricProviderType],
    provider
  };

  const isOrchFabric = isFabricProviderMatching(providerInfo, FabricProviderType.Orchestration);
  const isUnifiedFabricWithUpdateSupport =
    provider === SPARK_PROVIDER.DATABRICKS || provider === ORCHESTRATION_PROVIDER.Snowflake;

  const skipUpdateButton =
    isFabricProviderMatching(providerInfo, FabricProviderType.Airflow, AIRFLOW_PROVIDER.PROPHECY_MANAGED_TRIAL) ||
    // Skip update button for all older orchestration fabrics and new unified orchestration fabrics except dbx and snowflake
    (isUnifiedFabric ? isOrchFabric && !isUnifiedFabricWithUpdateSupport : isOrchFabric);

  const [isDataLoading, setIsDataLoading] = useDependantState<
    boolean,
    [typeof fabricProviderType, typeof initialFabricProviderTypes, typeof provider]
  >(
    (fabricProviderType, initialFabricProviderTypes, provider) => {
      // this is to avoid showing the data loading spinner when the fabric is loaded for unified fabrics
      if (initialFabricProviderTypes?.includes(FabricProviderType.Spark) && provider === SPARK_PROVIDER.DATABRICKS) {
        return false;
      }

      const providerInfo: FabricProviderInfoForMatching = {
        fabricProviderType: [fabricProviderType],
        provider
      };

      return (
        isFabricProviderMatching(providerInfo, FabricProviderType.Spark, SPARK_PROVIDER.DATABRICKS) ||
        isFabricProviderMatching(providerInfo, FabricProviderType.Spark, SPARK_PROVIDER.EMR) ||
        isFabricProviderMatching(providerInfo, FabricProviderType.Spark, SPARK_PROVIDER.SYNAPSE) ||
        isFabricProviderMatching(providerInfo, FabricProviderType.Spark, SPARK_PROVIDER.DATAPROC) ||
        isFabricProviderMatching(providerInfo, FabricProviderType.Airflow, AIRFLOW_PROVIDER.CLOUD_COMPOSER) ||
        isFabricProviderMatching(providerInfo, FabricProviderType.Airflow, AIRFLOW_PROVIDER.MWAA)
      );
    },
    [fabricProviderType, initialFabricProviderTypes, provider]
  );

  const onDataReady = usePersistentCallback(() => {
    afterAnimationFrame(() => {
      setIsDataLoading(false);
    });
  });

  return (
    <StyledSpinner spinning={isFabricLoading || isDataLoading}>
      <StyledStack gap={FabricTokens.Form.sectionGap} padding={`0 0 ${theme.spaces.x32}`} align='space-between'>
        <StackItem>
          {!isFabricLoading && (
            <Display show={!isDataLoading}>
              <ProviderForm
                fabricId={fabricId}
                formContext={formContexts[FABRIC_TABS.providers]}
                updateFabricErrors={setErrrors}
                disableProvider
                teamId={formContexts[FABRIC_TABS.basic].getValues('ownerUID')}
                onDataReady={onDataReady}
              />
            </Display>
          )}
        </StackItem>

        {!skipUpdateButton && (
          <Stack direction='horizontal' align='end' gap={theme.spaces.x12} padding={`0 ${theme.spaces.x4}`}>
            <ErrorTooltip
              errors={[errors, disabledMessage]}
              render={(hasErrors) => (
                <Button
                  variant='primary'
                  size='m'
                  loading={testConnectionLoading || isLoading}
                  disabled={hasErrors}
                  onClick={onUpdateFabric}
                  data-test-id='provider-tab-update-button'>
                  Update
                </Button>
              )}
            />
          </Stack>
        )}
      </StyledStack>
    </StyledSpinner>
  );
};
